import { useApolloClient, useQuery } from "@apollo/client";
import Check from "@mui/icons-material/Check";
import CommentOutlined from "@mui/icons-material/CommentOutlined";
import HelpOutline from "@mui/icons-material/HelpOutline";
import Home from "@mui/icons-material/Home";
import OpenInNew from "@mui/icons-material/OpenInNew";
import Visibility from "@mui/icons-material/Visibility";
import {
  Badge,
  Box,
  Button,
  Collapse,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Link,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog } from "components/ConfirmDialog";
import { IssueCount } from "components/IssueCount";
import { StatusChip } from "components/StatusChip";
import { SubmissionCommentsPanel } from "components/Submission/SubmissionCommentsPanel";
import {
  getStatusDisplay,
  submissionStatusChipColor,
} from "components/Submission/SubmissionStatusChipWithPopover";
import { TruncateTypography } from "components/TruncateTypography";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import {
  CommentThreadFragment,
  Permission,
  ReviewerStatus,
  SignatureStatus,
  SubmissionEncampStatus,
  SubmissionStatus,
  SubmissionStepStatus,
  SubmissionStepType,
} from "generated-graphql/graphql";
import { useAuthorization } from "hooks/useAuthorization";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { stepperStepPath } from "hooks/useStepperNavigation";
import { useDeleteSubmission } from "hooks/useSubmissionMutations";
import {
  getStepStatus,
  SubmissionStepState,
  useSubmissionIssuesGroupedByStep,
  useSubmissionStepStates,
} from "hooks/useSubmissionSteps";
import { ReactNode, useCallback, useMemo, useState } from "react";
import { Navigate, Outlet, useNavigate, useParams } from "react-router-dom";
import { isSubmissionFrozen } from "util/isSubmissionFrozen";
import { buildWasteFacilityUrl } from "util/wasteFacilityUrl";
import { stepperFlagForSubmission } from "util/wasteStepperFlags";
import { WASTE_SUBMISSION_STEP_PRESENTATION } from "util/wasteSubmissionStepMetadata";

import { useWasteStepperTour } from "../Announcement/useWasteStepperTour";
import { wasteStepAnchorId } from "../Announcement/wasteStepperAnnouncementConfig";
import { WasteStepperTour } from "../Announcement/WasteStepperTour";
import { getSavedWasteSubmissionsUrl } from "../navigation";
import { GET_SUBMISSION_STEPPER_COMMENTS_QUERY, GET_SUBMISSION_STEPPER_QUERY } from "./queries";
import { RejectionNotice } from "./RejectionNotice";
import { SubmissionStepStatusIcon } from "./SubmissionStepStatusIcon";
import { useSubmissionWasteActivityIssues } from "./useSubmissionWasteActivityIssues";
import { WasteStepperProvider } from "./WasteStepperContext";

const STATIC_VIEW_NAVIGATION_STATE = { staticView: true };

/** Past this the rail header crowds out the steps; the rest go to a "+N more" count. */
const MAX_FACILITY_NAMES_SHOWN = 3;

const FacilityInfo = ({
  loading,
  facilities,
  tenantId,
}: {
  loading: boolean;
  facilities: { id: string; name?: string | null; state?: string | null }[];
  tenantId: string;
}) => {
  const theme = useTheme();

  if (loading) {
    return (
      <Stack sx={{ px: 3, py: 2 }}>
        <Skeleton variant="text" height={theme.spacing(3)} />
        <Skeleton variant="text" />
      </Stack>
    );
  }

  const named = facilities.filter((f) => f.name);
  const states = Array.from(new Set(facilities.map((f) => f.state).filter(Boolean)));

  // One name per line: comma-joining them in a rail this narrow wraps mid-name,
  // so two facilities read as one mangled one.
  const shown = named.slice(0, MAX_FACILITY_NAMES_SHOWN);
  const overflow = named.length - shown.length;
  const caption = [states.join(", "), overflow > 0 ? `+${overflow} more` : ""]
    .filter(Boolean)
    .join(" · ");

  return (
    <Stack sx={{ px: 3, py: 2 }}>
      {shown.map((facility) => (
        <TruncateTypography key={facility.id} lines={2} fontWeight="medium">
          <Link
            href={buildWasteFacilityUrl(tenantId, facility.id)}
            target="_blank"
            rel="noopener noreferrer"
            color="inherit"
            sx={{ textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
          >
            {facility.name}
          </Link>
        </TruncateTypography>
      ))}
      {caption && (
        <Typography variant="caption" color="text.secondary">
          {caption}
        </Typography>
      )}
    </Stack>
  );
};

/**
 * Submission type / method + status chip, shown under the facility (mirrors the
 * TierII report sidebar's year/type + status chip block).
 */
const SubmissionInfo = ({
  loading,
  submissionType,
  submissionMethod,
  status,
  isDocumentForm,
}: {
  loading: boolean;
  submissionType?: string | null;
  submissionMethod?: string | null;
  status?: SubmissionStatus | null;
  isDocumentForm?: boolean;
}) => {
  const theme = useTheme();

  if (loading || !status) {
    return (
      <Box sx={{ px: 3, pt: 2, pb: 3 }}>
        <Skeleton variant="text" height={theme.spacing(4)} />
        <Skeleton variant="text" />
        <Skeleton variant="rounded" height={theme.spacing(4)} width={theme.spacing(14)} />
      </Box>
    );
  }

  return (
    <Box sx={{ px: 3, pt: 2, pb: 3 }}>
      <Box sx={{ mb: 2 }}>
        {submissionType && (
          <Typography variant="h6">{prettyPrintEnumValue(submissionType)}</Typography>
        )}
        {submissionMethod && (
          <Typography variant="caption" color="text.secondary">
            {prettyPrintEnumValue(submissionMethod)}
            {/* Caption text, not a chip — the rail can't fit two chips per line. */}
            {isDocumentForm ? " · Document submission" : ""}
          </Typography>
        )}
      </Box>
      <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", rowGap: 1 }}>
        <StatusChip
          variant="outlined"
          label={getStatusDisplay(status)}
          color={submissionStatusChipColor(status)}
        />
      </Stack>
    </Box>
  );
};

const StepNavItem = ({
  text,
  icon,
  selected,
  issueCount,
  onClick,
  anchorId,
}: {
  text: string;
  icon: ReactNode;
  selected: boolean;
  issueCount?: number;
  onClick: () => void;
  // Stable id for the announcement tour to highlight, independent of label changes.
  anchorId: string;
}) => {
  const theme = useTheme();
  return (
    <ListItem disablePadding id={anchorId}>
      <ListItemButton onClick={onClick} selected={selected} sx={{ pl: 3 }}>
        <ListItemIcon sx={{ minWidth: 0, mr: theme.spacing(2) }}>{icon}</ListItemIcon>
        <ListItemText primary={text} sx={{ mr: theme.spacing(1) }} />
        <IssueCount issueCount={issueCount} />
      </ListItemButton>
    </ListItem>
  );
};

/**
 * Shell for the guided waste submission steppers — the interactive 8700-12
 * (RCRAInfo myRCRAid) flow and the document-form (paper form) flow. Mirrors
 * the TierII report shell: a left sidebar (facility header + step nav), an
 * `<Outlet/>` for the active step, and a right-rail comments panel opened from
 * the comment icon at the content's top-right. Resolves the single facility
 * from the submission and provides shared state via `WasteStepperContext`.
 *
 * Submissions with no steps — and guided submissions whose archetype's stepper
 * flag is off — are redirected to the read-only submission view.
 */
export function WasteSubmissionStepper() {
  const params = useParams<{
    tenantId: string;
    submissionId: string;
    "*": string;
  }>();
  const { tenantId = "", submissionId = "" } = params;
  // The active step's route slug. The stepper index (overview) has an empty
  // slug; step routes append their slug (e.g. `.../stepper/contacts`).
  const selected = params["*"] ?? "";
  const navigate = useNavigate();
  const theme = useTheme();
  const { user, isStaff } = useCurrentUser();
  const { featureFlags, loading: flagsLoading } = useFeatureFlags();

  const submissionsUrl = getSavedWasteSubmissionsUrl(tenantId, user?.email);
  // One surface for editing, reviewing, signing, and read-only viewing, so the
  // leaf names the thing rather than claiming which of those the user is doing.
  useBreadcrumb([
    { label: "Waste", to: `/o/${tenantId}/waste` },
    { label: "Submissions", to: submissionsUrl },
    { label: "Submission" },
  ]);

  const { data, loading, error, refetch } = useQuery(GET_SUBMISSION_STEPPER_QUERY, {
    variables: { id: submissionId },
    skip: !submissionId,
    fetchPolicy: "cache-and-network",
  });
  const refetchSubmission = useCallback(async () => {
    await refetch();
  }, [refetch]);

  if (error) {
    throw error;
  }

  // The submission-wide comment thread lives in the right-rail panel so it's
  // reachable from every step. Rendered from the cache; optimistic add/delete
  // write back to the same cached query.
  const apolloClient = useApolloClient();
  const { data: commentsData } = useQuery(GET_SUBMISSION_STEPPER_COMMENTS_QUERY, {
    variables: { id: submissionId },
    skip: !submissionId,
    fetchPolicy: "cache-and-network",
  });
  const comments = commentsData?.submission?.comments ?? [];

  const updateCachedComments = (
    updater: (prev: CommentThreadFragment[]) => CommentThreadFragment[]
  ) => {
    apolloClient.cache.updateQuery(
      {
        query: GET_SUBMISSION_STEPPER_COMMENTS_QUERY,
        variables: { id: submissionId },
      },
      (existing) =>
        existing?.submission
          ? {
              submission: {
                ...existing.submission,
                comments: updater(existing.submission.comments ?? []),
              },
            }
          : existing
    );
  };

  // Comments live in a right-rail panel opened from the comment icon button.
  // "Unread" is tracked client-side: the icon badge counts comments newer than
  // the last time this browser opened the panel, and those comments are
  // highlighted in the thread for that viewing (opening marks them read, so
  // the highlight is gone on the next visit — per the Figma annotation).
  const [commentsOpen, setCommentsOpen] = useState(false);
  // Watermark to highlight against while the panel is open; undefined = no
  // highlighting (panel was opened without unread state), null = never viewed
  // before (highlight everything).
  const [highlightAfter, setHighlightAfter] = useState<string | null | undefined>(undefined);
  const commentsViewedKey = `wasteSubmissionCommentsViewedAt:${submissionId}`;
  const [commentsViewedAt, setCommentsViewedAt] = useState<string | null>(() =>
    localStorage.getItem(commentsViewedKey)
  );
  // Your own comments are read by definition — without this, adding a comment
  // while the panel is open would immediately badge (and later highlight) it.
  const unreadCommentCount = comments.filter(
    (c) =>
      !(user?.id && c.user?.id === user.id) &&
      (!commentsViewedAt || new Date(c.createdAt) > new Date(commentsViewedAt))
  ).length;

  const markCommentsViewed = () => {
    const now = new Date().toISOString();
    localStorage.setItem(commentsViewedKey, now);
    setCommentsViewedAt(now);
  };

  const toggleComments = () => {
    if (commentsOpen) {
      // Catch anything that arrived while the panel was open.
      markCommentsViewed();
      setCommentsOpen(false);
      return;
    }
    setHighlightAfter(commentsViewedAt);
    markCommentsViewed();
    setCommentsOpen(true);
  };

  const submission = data?.submission;
  const facilityId = submission?.facilitySubmissions?.[0]?.facility?.id ?? "";
  const facilities = (submission?.facilitySubmissions ?? []).map((fs) => fs.facility);

  // CE issues on the saved Waste Activity row drive that step's status the same
  // way facility issues drive the others (a saved-but-flagged step isn't "Done").
  const wasteActivityIssues = useSubmissionWasteActivityIssues(
    submissionId,
    submission?.wasteActivity ?? null
  );
  const stepStates = useSubmissionStepStates(
    submission?.stepperSteps ?? [],
    facilityId,
    submission?.status,
    wasteActivityIssues,
    submissionId
  );
  const issuesByStep = useSubmissionIssuesGroupedByStep(
    facilityId,
    wasteActivityIssues,
    submissionId
  );
  const isFrozen = isSubmissionFrozen(submission?.status);
  // Processing freezes content, but the filing hasn't happened yet.
  const filingOutstanding = submission?.status === SubmissionStatus.ProcessingSubmission;
  // Nothing is actionable until rejection routing resolves the rejection.
  const isRejected = submission?.encampStatus === SubmissionEncampStatus.Rejected;
  // usesInteractiveStepper, not the (type, method) combo — matches the API's
  // canEditStepperSubmissionFacility.
  const isDocumentForm = submission ? !submission.usesInteractiveStepper : false;
  // Editing mirrors the API's stepper authorization so the shell never offers
  // edit affordances the server would reject: interactive forms (8700-12) gate
  // on the RCRA facility write permission; document-form editing is staff-only
  // until customer self-service gets an explicit permission decision. A frozen
  // submission's data is read-only for everyone, so editing also stops once
  // frozen, and the same is true while a rejection is outstanding.
  const { hasPermissionForFacility } = useAuthorization();
  const canEditFacility = facilityId
    ? hasPermissionForFacility(facilityId, [Permission.WriteFacilityRcra])
    : false;
  const canEditSubmission = isDocumentForm ? isStaff : canEditFacility;
  const canEdit = canEditSubmission && !isFrozen && !isRejected;

  // The steps the rail actually renders. Shared with the announcement tour so
  // it never points an anchor at a row that isn't there.
  const railSteps = useMemo(
    () =>
      (submission?.stepperSteps ?? [])
        .map(({ step }) => step)
        .filter((step) => step !== SubmissionStepType.SubmissionDetails),
    [submission?.stepperSteps]
  );

  // Plain callback, not `useStepperNavigation` — this component isn't a descendant of the `WasteStepperProvider` it renders.
  const goToTourStep = useCallback(
    (step: SubmissionStepType, options?: { replace?: boolean }) =>
      navigate(
        stepperStepPath(tenantId, submissionId, WASTE_SUBMISSION_STEP_PRESENTATION[step].routeSlug),
        options
      ),
    [navigate, tenantId, submissionId]
  );

  const tour = useWasteStepperTour({
    isDocumentForm,
    hasCitations: (submission?.requirementCitations ?? []).length > 0,
    presentSteps: railSteps,
    isOnUnderstandStep:
      selected === WASTE_SUBMISSION_STEP_PRESENTATION[SubmissionStepType.Understand].routeSlug,
    goToStep: goToTourStep,
  });

  // A reviewer's rail reflects their own participation; staff keep shared state.
  const myReviewer = (submission?.reviewers ?? []).find((r) => r.user.id === user?.id);
  const prepareDocumentStatus = (submission?.stepperSteps ?? []).find(
    ({ step }) => step === SubmissionStepType.PrepareDocument
  )?.status;
  const reviewerPreparationStatus = (submission?.stepperSteps ?? []).find(
    ({ step }) => step === SubmissionStepType.ReviewerPreparation
  )?.status;
  const displayStepStates = useMemo(() => {
    if (isStaff || !myReviewer) return stepStates;
    // Approved is done; pending or rejected still needs them. Signers must sign too.
    const myParticipationComplete =
      myReviewer.reviewerStatus === ReviewerStatus.Approved &&
      (!myReviewer.isSigner || myReviewer.signatureStatus === SignatureStatus.Completed);
    return {
      ...stepStates,
      [SubmissionStepType.Understand]: myReviewer.acknowledgedAt
        ? SubmissionStepState.Done
        : SubmissionStepState.InProgress,
      // Document prep and reviewer/signer prep are staff work, so show plain
      // status instead of Invalid.
      [SubmissionStepType.PrepareDocument]: getStepStatus({
        stepStatus: prepareDocumentStatus,
        hasIssues: false,
      }),
      [SubmissionStepType.ReviewerPreparation]: getStepStatus({
        stepStatus: reviewerPreparationStatus,
        hasIssues: false,
      }),
      [SubmissionStepType.ReviewAndSign]: myParticipationComplete
        ? SubmissionStepState.Done
        : SubmissionStepState.InProgress,
    };
  }, [stepStates, isStaff, myReviewer, prepareDocumentStatus, reviewerPreparationStatus]);

  // Same reasoning as the step state above: don't badge a reviewer with staff
  // work. Reviewer Preparation and Review & Sign are zeroed too — their issues
  // are reviewer/signature configuration, which staff own, and Review & Sign's
  // display state above is already the reviewer's own participation rather
  // than getStepStatus's Invalid.
  // A new submission is incomplete by definition, so badging every step on
  // arrival reads as broken rather than as work to do. Flag a step only once
  // someone has been through it and left it unresolved.
  const startedIssuesByStep = useMemo(() => {
    const started = new Set(
      (submission?.stepperSteps ?? [])
        .filter(({ status }) => status !== SubmissionStepStatus.NotStarted)
        .map(({ step }) => step)
    );
    return Object.fromEntries(
      Object.entries(issuesByStep).map(([step, stepIssues]) => [
        step,
        started.has(step as SubmissionStepType) ? stepIssues : [],
      ])
    );
  }, [issuesByStep, submission?.stepperSteps]);

  const displayIssuesByStep = useMemo(() => {
    if (isStaff || !myReviewer) return startedIssuesByStep;
    return {
      ...startedIssuesByStep,
      [SubmissionStepType.PrepareDocument]: [],
      [SubmissionStepType.ReviewerPreparation]: [],
      [SubmissionStepType.ReviewAndSign]: [],
    };
  }, [startedIssuesByStep, isStaff, myReviewer]);

  // Staff delete: blocked while rejected (resolve via rejection routing first)
  // or while revision submissions still link here.
  const alerts = useAlerts();
  const [deleteSubmission, { loading: deletingSubmission }] = useDeleteSubmission();
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const hasLinkedRevisions = (submission?.revisionSubmissions?.length ?? 0) > 0;

  const handleDeleteSubmission = async () => {
    try {
      await deleteSubmission({ variables: { id: submissionId } });
      alerts.success("Submission deleted successfully");
      navigate(submissionsUrl);
    } catch (error) {
      alerts.error("Failed to delete submission", error);
    }
  };

  const sideBarWidth = theme.spacing(32);
  // ~310px per the Figma comments panel ("slider", 309px at the 1440 breakpoint).
  const commentsPanelWidth = theme.spacing(38.75);
  const topNavHeight = theme.spacing(15.25);
  // The step column's padding, kept as a number so the scrollport height below
  // stays right when it changes.
  const stepContentPadding = 3;
  const shellHeight = `calc(100vh - ${topNavHeight})`;
  const stepScrollHeight = `calc(${shellHeight} - ${theme.spacing(stepContentPadding * 2)})`;

  // Every submission has steps once backfilled, so routing is purely
  // flag-driven — interactive forms check the interactive-stepper flag,
  // document forms the document-stepper flag. Waits for the flag map so a
  // still-loading (all-false) map doesn't bounce a valid stepper submission.
  // Document forms in review/signing stay here — Review & sign owns that surface.
  if (
    submission &&
    !flagsLoading &&
    featureFlags[stepperFlagForSubmission(submission.usesInteractiveStepper)] !== true
  ) {
    return <Navigate to={`/o/${tenantId}/waste/submissions/${submissionId}`} replace />;
  }

  return (
    <WasteStepperProvider
      value={{
        submissionId,
        facilityId,
        // `submission` is undefined while loading; the shell renders skeletons in
        // that case and only the provider's consumers (mounted via Outlet) need a
        // resolved submission, which is guaranteed once content renders below.
        submission: submission ?? null,
        refetchSubmission,
        stepStates: displayStepStates,
        isFrozen,
        isRejected,
        canEdit,
      }}
    >
      <WasteStepperTour
        steps={tour.steps}
        phase={tour.phase}
        isPending={tour.isPending}
        dismissModal={tour.dismissModal}
        softDismissModal={tour.softDismissModal}
        dontShowAgain={tour.dontShowAgain}
        setDontShowAgain={tour.setDontShowAgain}
        startTour={tour.startTour}
        dismissTour={tour.dismissTour}
        completeTour={tour.completeTour}
        viewTourStep={tour.viewTourStep}
      />
      {/*
       * `nowrap` + `minWidth: 0` (content item) keep all three columns on one
       * flex line. Grid containers wrap by default and a flex item's implicit
       * minimum is its min-content width — the Review step's reviewers DataGrid
       * sets inline pixel column widths measured pre-panel, so opening the
       * comments panel made the line overflow and wrap it to the bottom.
       */}
      <Grid container sx={{ height: shellHeight, flexWrap: "nowrap" }}>
        <Grid
          sx={{
            width: sideBarWidth,
            flexShrink: 0,
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Stack sx={{ flexShrink: 0 }}>
            <FacilityInfo
              loading={loading && !submission}
              facilities={facilities}
              tenantId={tenantId}
            />
            <Divider />
            <SubmissionInfo
              loading={loading && !submission}
              submissionType={submission?.submissionType}
              submissionMethod={submission?.submissionMethod}
              status={submission?.status}
              isDocumentForm={isDocumentForm}
            />
            <List sx={{ p: 0 }}>
              {railSteps.map((step) => {
                const presentation = WASTE_SUBMISSION_STEP_PRESENTATION[step];
                const isSelected = selected === presentation.routeSlug;
                // Overview is the home of the flow (Home icon, like the Tier II
                // report). Once frozen the submission is read-only/filed, so the
                // remaining steps show grey checks rather than live status icons
                // (mirrors the Tier II verified state) — except Submit & File
                // while the filing is outstanding.
                let icon: ReactNode;
                if (step === SubmissionStepType.Overview) {
                  icon = <Home color="action" />;
                } else if (
                  isFrozen &&
                  !(filingOutstanding && step === SubmissionStepType.SubmitAndFile)
                ) {
                  icon = <Check color="disabled" />;
                } else {
                  icon = <SubmissionStepStatusIcon status={displayStepStates[step]} />;
                }
                // Once frozen, the Overview is the read-only "Filing Progress"
                // landing — relabel its nav entry to match (mirrors the Tier II
                // report's verified `Filing Progress` title). UI-only; the step
                // type stays OVERVIEW.
                const label =
                  step === SubmissionStepType.Overview && isFrozen
                    ? "Filing Progress"
                    : presentation.label;
                return (
                  <StepNavItem
                    key={step}
                    anchorId={wasteStepAnchorId(step)}
                    text={label}
                    icon={icon}
                    selected={isSelected}
                    issueCount={isFrozen ? undefined : displayIssuesByStep[step]?.length}
                    onClick={() =>
                      navigate(stepperStepPath(tenantId, submissionId, presentation.routeSlug))
                    }
                  />
                );
              })}
            </List>
            {tour.eligible && (
              <Stack alignItems="flex-start" sx={{ px: 3, py: 1 }}>
                <Tooltip title="Show me around again">
                  <IconButton
                    size="small"
                    color="secondary"
                    aria-label="Show me around again"
                    onClick={tour.reopenModal}
                  >
                    <HelpOutline fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            )}
          </Stack>
          {isStaff && submission && (
            <Stack
              alignItems="flex-start"
              spacing={0.5}
              sx={{ mt: "auto", px: 3, py: 2, flexShrink: 0 }}
            >
              {/* The read-only view redirects stepper submissions back here, so
                  Review & Sign's static display is the stepper's View Submission. */}
              {(submission.stepperSteps ?? []).some(
                ({ step }) => step === SubmissionStepType.ReviewAndSign
              ) && (
                <Button
                  variant="text"
                  color="staff"
                  endIcon={<Visibility />}
                  onClick={() =>
                    navigate(
                      stepperStepPath(
                        tenantId,
                        submissionId,
                        WASTE_SUBMISSION_STEP_PRESENTATION[SubmissionStepType.ReviewAndSign]
                          .routeSlug
                      ),
                      { state: STATIC_VIEW_NAVIGATION_STATE }
                    )
                  }
                >
                  View Submission
                </Button>
              )}
              <Button
                component="a"
                href={`/staff/fulfillment/waste/submissions/${submissionId}`}
                target="_blank"
                variant="text"
                color="staff"
                endIcon={<OpenInNew />}
              >
                Open Fulfillment
              </Button>
              <Tooltip
                title={
                  hasLinkedRevisions
                    ? "This submission has linked revision submissions. Delete the revisions first."
                    : ""
                }
              >
                <span>
                  <Button
                    variant="text"
                    color="error"
                    disabled={deletingSubmission || isRejected || hasLinkedRevisions}
                    onClick={() => setConfirmDeleteOpen(true)}
                  >
                    Delete Submission
                  </Button>
                </span>
              </Tooltip>
            </Stack>
          )}
        </Grid>
        <Grid
          size="grow"
          sx={{
            // Shrinkable below min-content so the fixed-width rails always fit
            // on the single flex line (see the container comment).
            minWidth: 0,
            borderLeft: `1px solid ${theme.palette.divider}`,
            width: `calc(100% - ${sideBarWidth})`,
            maxWidth: "1200px",
            display: "flex",
            gap: 1,
            p: stepContentPadding,
          }}
        >
          {/*
           * The shell is fixed-height (the Grid container is sized to the
           * viewport), so the content scrolls inside this box rather than
           * page-scrolling — keeping the sidebar, comment icon, and comments
           * panel pinned. `pb` keeps the step's footer button off the bottom
           * edge on tall steps (otherwise content spilled past the column's
           * padding and the button sat flush against the page bottom).
           */}
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              overflowY: "auto",
              // This scrollport's height, for step content sized against it
              // (the Review & Sign document preview).
              "--stepper-scroll-height": stepScrollHeight,
              // Clip horizontal overflow so no stepper section shows a
              // page-level horizontal scrollbar; wide children (e.g. the
              // reviewers / waste-streams data grids) scroll within their own
              // bounded width instead. `minWidth: 0` lets those children shrink
              // to the column rather than forcing it wider.
              overflowX: "hidden",
              // MUI's FormControlLabel sets marginLeft: -11px, so switches and
              // radios sit just outside this box and lose a couple of pixels to
              // the clip. Widen the box left by that overhang and pad it back so
              // they render whole without moving the content.
              ml: "-11px",
              pl: "11px",
              // Keep the column scrollable but hide the scrollbar track — the
              // step content shouldn't show a right-hand scrollbar (per design).
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": { display: "none" },
              pb: 1,
            }}
          >
            {!submission ? (
              <Box>
                <Skeleton variant="text" height={theme.spacing(4)} />
                <Skeleton variant="text" />
                <Skeleton variant="text" />
              </Box>
            ) : (
              <>
                <RejectionNotice submissionId={submissionId} />
                <Outlet />
              </>
            )}
          </Box>
          {/*
           * Comment icon in its own gutter column at the content's top-right
           * (the step headers' title row, per the Figma placement). A real flow
           * column — not an absolute overlay — so full-width step content like
           * the frozen-submission banner ends beside the icon instead of
           * rendering underneath it. The badge counts comments that arrived
           * since this browser last opened the panel.
           */}
          <Box sx={{ flexShrink: 0, alignSelf: "flex-start" }}>
            <Tooltip title={commentsOpen ? "Hide comments" : "View comments"}>
              <IconButton
                aria-label={commentsOpen ? "Hide comments" : "View comments"}
                onClick={toggleComments}
              >
                <Badge badgeContent={unreadCommentCount} color="secondary" max={999}>
                  <CommentOutlined />
                </Badge>
              </IconButton>
            </Tooltip>
          </Box>
        </Grid>
        {/*
         * Right-rail comments panel (Figma "slider"): pinned to the viewport's
         * right edge (`ml: auto` absorbs any slack the content column's
         * max-width leaves), full height, opened from the comment icon. A
         * horizontal Collapse slides it in/out (animating its width) rather than
         * popping abruptly; `unmountOnExit` keeps it inert when closed but still
         * animates the close before unmounting.
         */}
        {submission && (
          <Collapse
            in={commentsOpen}
            orientation="horizontal"
            unmountOnExit
            sx={{
              flexShrink: 0,
              height: "100%",
              ml: "auto",
              "& .MuiCollapse-wrapper, & .MuiCollapse-wrapperInner": { height: "100%" },
            }}
          >
            <Box
              sx={{
                width: commentsPanelWidth,
                height: "100%",
                borderLeft: `1px solid ${theme.palette.divider}`,
              }}
            >
              <SubmissionCommentsPanel
                submissionId={submissionId}
                comments={comments}
                highlightAfter={highlightAfter}
                onClose={toggleComments}
                commentAdded={(comment) => updateCachedComments((prev) => [...prev, comment])}
                commentDeleted={(commentId) =>
                  updateCachedComments((prev) => prev.filter((c) => c.id !== commentId))
                }
              />
            </Box>
          </Collapse>
        )}
      </Grid>

      {confirmDeleteOpen && (
        <ConfirmDialog
          open={confirmDeleteOpen}
          onClose={() => setConfirmDeleteOpen(false)}
          onConfirm={() => {
            setConfirmDeleteOpen(false);
            void handleDeleteSubmission();
          }}
          header="Are you sure you want to delete this submission?"
          info="This action cannot be undone"
          confirmText="YES, DELETE"
          cancelText="CANCEL"
          minWidth="440px"
          maxWidth="440px"
        />
      )}
    </WasteStepperProvider>
  );
}
